import React, { useState } from "react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input, Label } from "../ui/input";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { toast } from "sonner";
import { Mail, ArrowRight, ArrowLeft, ShieldCheck, Inbox } from "lucide-react";

export function EmailStage({ sessionId, onBack, onVerified }) {
  const [phase, setPhase] = useState("form"); // form | code
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role_title: "" });
  const [issued, setIssued] = useState(null);
  const [code, setCode] = useState("");

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding/email/start", {
        session_id: sessionId, name: form.name, email: form.email, role_title: form.role_title || null,
      });
      setIssued(data);
      setPhase("code");
      toast.success("Verification code sent — check your inbox");
    } catch (e) { toast.error(apiErr(e, "Could not send verification code")); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/onboarding/email/verify", { session_id: sessionId, code: code.trim() });
      toast.success("Email verified");
      onVerified({ email: issued?.email, name: form.name });
    } catch (e) { toast.error(apiErr(e, "Verification failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl" data-testid="email-stage">
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="lime">COMPANY EMAIL</Badge>
        <span className="text-xs text-muted-foreground font-mono">Verify your work email to begin</span>
      </div>

      {phase === "form" && (
        <>
          <h2 className="font-display text-3xl font-bold tracking-tight">Start your trial with your company email</h2>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">
            Use your work email address — personal domains (Gmail, Yahoo, Outlook…) aren't accepted.
            We'll email you a 6-digit code to verify it.
          </p>
          <div className="mt-6 space-y-4 card-flat bg-surface p-5">
            <div>
              <Label>Full name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Amanda Lee" data-testid="email-name-input" />
            </div>
            <div>
              <Label>Company email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@yourcompany.com.sg" data-testid="email-email-input" />
            </div>
            <div>
              <Label>Role title (optional)</Label>
              <Input value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} placeholder="e.g. Sustainability Manager" data-testid="email-role-input" />
            </div>
          </div>
          <div className="flex items-center gap-3 mt-7">
            <Button variant="outline" onClick={onBack} data-testid="email-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button variant="primary" size="lg" className="gap-2" disabled={busy || form.name.trim().length < 2 || !form.email.includes("@")} onClick={start} data-testid="email-send-code-btn">
              <Mail className="h-4 w-4" /> {busy ? "Sending…" : "Email me a code"}
            </Button>
          </div>
        </>
      )}

      {phase === "code" && (
        <>
          <h2 className="font-display text-3xl font-bold tracking-tight">Enter your verification code</h2>
          <p className="text-muted-foreground text-sm mt-2 max-w-lg">
            We've emailed a 6-digit code to <span className="text-foreground font-semibold">{issued?.email}</span>. It expires in {issued?.expires_in_minutes} minutes.
          </p>
          <div className="card-flat bg-surface p-4 mt-6 border-accent/40 flex items-start gap-3" data-testid="email-inbox-hint">
            <Inbox className="h-5 w-5 text-accent shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground leading-relaxed">
              It can take a minute to arrive. Not seeing it? Check your spam/junk folder, then use
              <span className="text-foreground font-medium"> Resend code</span> below.
            </div>
          </div>
          <div className="mt-5 max-w-xs">
            <Label>6-digit code *</Label>
            <Input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="font-mono text-lg tracking-[0.3em]" placeholder="••••••" data-testid="email-code-input" />
          </div>
          <div className="flex items-center gap-3 mt-6 flex-wrap">
            <Button variant="outline" onClick={() => setPhase("form")} data-testid="email-code-back-btn"><ArrowLeft className="h-4 w-4" /> Back</Button>
            <Button variant="ghost" onClick={start} disabled={busy} data-testid="email-resend-btn">Resend code</Button>
            <Button variant="primary" size="lg" className="gap-2" disabled={busy || code.length !== 6} onClick={verify} data-testid="email-verify-btn">
              <ShieldCheck className="h-4 w-4" /> {busy ? "Verifying…" : "Verify email"} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
